import React, { useState, useEffect, useCallback, useRef } from 'react';
import { IconButton, Collapse, ToggleButton, ToggleButtonGroup, CircularProgress as MuiCircularProgress, Tooltip } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Typography,
  Grid,
  Paper,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Badge
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Help as HelpIcon,
  PersonalInjury as PersonalInjuryIcon
} from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import dayjs from 'dayjs';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import SpareAvailabilityTable from '../../../components/rsvp/SpareAvailabilityTable';
import SpareEmailComposer from '../../../components/spares/SpareEmailComposer';
import OpenSparesChatButton from '../../../components/chat/OpenSparesChatButton';
import { useIsSparesCoordinator, useIsAdmin, useIsReporter } from '../../../contexts/PermissionsContext';
import { FEATURES, useFeature } from '../../../contexts/FeatureContext';


const PlayerAvailabilityPage = () => {
  // const { leagueId } = useParams();

  // State for calendar
  const [selectedDate, setSelectedDate] = useState(null); // Will store Dayjs object
  const { leagueId } = useParams();
  const [gameDates, setGameDates] = useState([]);
  const [teamAvailability, setTeamAvailability] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Filter state for showing/hiding players by status
  const [showAvailable, setShowAvailable] = useState(true);
  const [showUnavailable, setShowUnavailable] = useState(true);
  const [showUnknown, setShowUnknown] = useState(true);
  // The league's spare pool (null when the league has none)
  const [sparePool, setSparePool] = useState(null);
  // Filter state for showing/hiding spares by status
  const [showSpareAvailable, setShowSpareAvailable] = useState(true);
  const [showSpareUnavailable, setShowSpareUnavailable] = useState(true);
  const [showSpareNoResponse, setShowSpareNoResponse] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [isCacheFresh, setIsCacheFresh] = useState(false);
  const cacheTimerRef = useRef(null);
  const CACHE_DURATION_MS = 60_000; // 1 minute
  const isSparesCoordinator = useIsSparesCoordinator();
  const sparesEnabled = useFeature(FEATURES.SPARES, leagueId);
  const isAdmin = useIsAdmin();
  const isReporter = useIsReporter();
  const canManageSparePool = isSparesCoordinator || isAdmin;
  // Fetch game dates for the league
  const fetchGameDates = useCallback(async () => {
    if (!leagueId) return;
    try {
      const data = await unifiedApi.get(`/api/leagues/${leagueId}/game-dates`);
      setGameDates(data.gameDates || []);
    } catch (err) {
      setError(`Failed to load game dates: ${err.message}`);
    }
  }, [leagueId]);

  // Fetch team availability for selected date
  const fetchTeamAvailability = useCallback(async (date) => {
    if (!leagueId || !date) return;
    setLoading(true);
    setError(null);
    try {
      const dateStr = date.format ? date.format('YYYY-MM-DD') : date;
      const data = await unifiedApi.get(`/api/leagues/${leagueId}/availability?date=${dateStr}`);
      const flattenedTeams = (data.teams || []).flatMap(team =>
        (team.games || []).map((game, gameIndex) => ({
          teamId: team.teamId,
          teamName: team.teamName,
          teamSlug: team.teamSlug,
          gameIndex,
          game: {
            startTime: game.startTime || null,
            fieldName: game.fieldName || null,
            opponentName: game.opponentName || null,
          },
          players: (game.players || []).map(player => ({
            playerId: player.userId,
            userTenantId: player.userTenantId,
            playerName: `${player.firstName} ${player.lastName}`,
            position: player.position || 'N/A',
            status: player.status,
            isInjured: player.isInjured || false
          }))
        }))
      );
      setTeamAvailability(flattenedTeams);
      const now = new Date();
      setLastFetched(now);
      setIsCacheFresh(true);
      // Schedule cache expiry
      if (cacheTimerRef.current) clearTimeout(cacheTimerRef.current);
      cacheTimerRef.current = setTimeout(() => setIsCacheFresh(false), CACHE_DURATION_MS);
    } catch (err) {
      setError(`Failed to load team availability: ${err.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [leagueId]);

  // Clean up cache timer on unmount
  useEffect(() => {
    return () => { if (cacheTimerRef.current) clearTimeout(cacheTimerRef.current); };
  }, []);

  // Refresh handler with cache check
  const handleRefresh = useCallback(() => {
    if (!selectedDate || isCacheFresh) return;
    setRefreshing(true);
    fetchTeamAvailability(selectedDate);
  }, [selectedDate, fetchTeamAvailability, isCacheFresh]);

  useEffect(() => {
    fetchGameDates();
  }, [fetchGameDates]);

  // Fetch the league's spare pool
  useEffect(() => {
    const fetchSparePool = async () => {
      if (!leagueId || !sparesEnabled) {
        setSparePool(null);
        return;
      }
      try {
        setSparePool(await unifiedApi.getLeagueSparePool(leagueId));
      } catch (err) {
        // Non-critical — spare section simply won't show
        console.error('Failed to load spare pool:', err);
      }
    };
    fetchSparePool();
  }, [leagueId, sparesEnabled]);

  useEffect(() => {
    if (selectedDate) {
      fetchTeamAvailability(selectedDate);
    }
  }, [selectedDate, fetchTeamAvailability]);
  // State for calendar visibility
  const [calendarOpen, setCalendarOpen] = useState(true);

  // Helper function to check if a date has games
  const hasGames = (date) => {
    // Use the local calendar-day components (not UTC) so the calendar matches
    // the organization-timezone game dates returned by the API. Using
    // toISOString() here would shift the day for users in timezones ahead of
    // UTC (e.g. Prague), causing games to appear on the wrong day.
    const dateStr = dayjs(date).format('YYYY-MM-DD');
    const found = gameDates.find(gd => {
      // Handle both string dates and ISO date strings
      const gameDate = gd.date instanceof Date ? gd.date.toISOString().split('T')[0] : 
                     (typeof gd.date === 'string' ? gd.date.split('T')[0] : gd.date);
      return gameDate === dateStr;
    });
    return found;
  };



  // Custom day component for the calendar
  const CustomDay = (props) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const gameDate = hasGames(day.toDate());
    
    if (gameDate) {
      return (
        <Badge
          key={day.toString()}
          overlap="circular"
          badgeContent={
            <Chip
              label={gameDate.gameCount}
              size="small"
              color="primary"
              onClick={() => {/* do nothing */}}
              sx={{ fontSize: '0.6rem', height: 16, pointerEvents: 'none' }}
            />
          }
        >
          <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />
        </Badge>
      );
    }

    return <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />;
  };

  // Handle date selection
  const handleDateChange = (newDate) => {
    if (newDate) {
      setSelectedDate(newDate); // Store as Dayjs object
      setCalendarOpen(false); // Auto-collapse calendar after picking
    }
  };

  const getAvailabilityIcon = (status) => {
    // Handle boolean values (true = available, false = unavailable)
    if (status === true) {
      return <CheckCircleIcon color="success" />;
    } else if (status === false) {
      return <CancelIcon color="error" />;
    }
    
    // Handle string values
    const statusStr = typeof status === 'string' ? status.toLowerCase() : String(status).toLowerCase();
    switch (statusStr) {
      case 'available':
      case 'true':
        return <CheckCircleIcon color="success" />;
      case 'unavailable':
      case 'false':
        return <CancelIcon color="error" />;
      default:
        return <HelpIcon color="warning" />;
    }
  };

  const getGameDisplay = (game) => {
    const hasScheduledTime = Boolean(game.startTime);
    const opponentName = game.opponentName?.trim();
    const isByeWeek = hasScheduledTime && !opponentName;
    const details = [
      game.startTime,
      game.fieldName,
      isByeWeek ? 'BYE week' : opponentName ? `vs ${opponentName}` : null,
    ].filter(Boolean).join(' · ');

    return { details, isByeWeek };
  };



  return (
    <AdminLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Player Availability
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" color="text.secondary">
              View team and player availability for scheduled game dates
            </Typography>
            {lastFetched && (
              <Typography variant="caption" color="text.disabled" data-testid="last-fetched">
                · Last updated: {lastFetched.toLocaleTimeString()}
              </Typography>
            )}
          </Box>
          {/* Not tied to a game date: the button shows whenever the user belongs to the league's spares chat. */}
          <OpenSparesChatButton leagueId={leagueId} sx={{ mt: 1 }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {selectedDate && (
            <Tooltip title={isCacheFresh ? 'Data is less than 1 minute old' : 'Refresh availability data'}>
              <span>
                <IconButton
                  aria-label="Refresh availability"
                  onClick={handleRefresh}
                  disabled={refreshing || loading || isCacheFresh}
                  size="large"
                  data-testid="refresh-button"
                >
                  {refreshing ? <MuiCircularProgress size={24} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton
            aria-label={calendarOpen ? 'Hide calendar' : 'Show calendar'}
            onClick={() => setCalendarOpen((open) => !open)}
            sx={{ ml: 1 }}
            size="large"
          >
            <CalendarMonthIcon />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={calendarOpen}>
        <Paper sx={{ p: 3, mb: 3, maxWidth: 400, ml: 'auto' }}>
          <Typography variant="h6" gutterBottom textAlign="center">
            Select a Game Date
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <DateCalendar
              defaultValue={dayjs()}
              onChange={handleDateChange}
              slots={{
                day: CustomDay,
              }}
              shouldDisableDate={(date) => !hasGames(date.toDate())}
              sx={{
                '& .MuiPickersDay-root': {
                  '&.Mui-disabled': {
                    color: 'text.disabled',
                  },
                },
              }}
            />
          </Box>
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, fontSize: '0.75rem', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: 'primary.main', borderRadius: 1 }} />
              <Typography variant="caption">Dates with games</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: 'text.disabled', borderRadius: 1 }} />
              <Typography variant="caption">No games scheduled</Typography>
            </Box>
          </Box>
        </Paper>
      </Collapse>

        {/* Team Availability Section */}
        <Grid size={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Team Availability
            </Typography>

            {!selectedDate && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Select a date with games to view team availability
                </Typography>
              </Box>
            )}

            {selectedDate && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {selectedDate.format('dddd, MMMM D, YYYY')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Show:</Typography>
                  <ToggleButtonGroup size="small">
                    <ToggleButton 
                      value="available" 
                      selected={showAvailable}
                      onChange={() => setShowAvailable(!showAvailable)}
                      sx={{ gap: 0.5 }}
                    >
                      <CheckCircleIcon color={showAvailable ? "success" : "disabled"} fontSize="small" />
                      <Typography variant="caption">Going</Typography>
                    </ToggleButton>
                    <ToggleButton 
                      value="unavailable" 
                      selected={showUnavailable}
                      onChange={() => setShowUnavailable(!showUnavailable)}
                      sx={{ gap: 0.5 }}
                    >
                      <CancelIcon color={showUnavailable ? "error" : "disabled"} fontSize="small" />
                      <Typography variant="caption">Not Going</Typography>
                    </ToggleButton>
                    <ToggleButton 
                      value="unknown" 
                      selected={showUnknown}
                      onChange={() => setShowUnknown(!showUnknown)}
                      sx={{ gap: 0.5 }}
                    >
                      <HelpIcon color={showUnknown ? "warning" : "disabled"} fontSize="small" />
                      <Typography variant="caption">No Response</Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
            )}

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {selectedDate && !loading && teamAvailability.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No team availability data found for this date.
              </Typography>
            )}

            {selectedDate && !loading && (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(auto-fill, minmax(240px, 1fr))'
                },
                gridAutoRows: 'auto',
                gap: 2
              }}>
                {teamAvailability.map((team) => {
                  // Define position order for sorting
                  const positionOrder = { 'GK': 1, 'FB': 2, 'DF': 2, 'MF': 3, 'FW': 4 };
                  
                  // Helper function to sort by position then name
                  const sortByPositionThenName = (a, b) => {
                    const posA = positionOrder[a.position] || 999;
                    const posB = positionOrder[b.position] || 999;
                    if (posA !== posB) {
                      return posA - posB;
                    }
                    return a.playerName.localeCompare(b.playerName);
                  };
                  
                  // Separate injured players first (they don't count in availability)
                  const nonInjuredPlayers = (team.players || []).filter(p => !p.isInjured);
                  const injuredPlayers = (team.players || []).filter(p => p.isInjured).sort(sortByPositionThenName);

                  // Categorize non-injured players by availability status
                  const available = nonInjuredPlayers.filter(p => {
                    const s = typeof p.status === 'string' ? p.status.toLowerCase() : p.status;
                    return s === 'available' || s === true || s === 'true';
                  }).sort(sortByPositionThenName);
                  
                  const unavailable = nonInjuredPlayers.filter(p => {
                    const s = typeof p.status === 'string' ? p.status.toLowerCase() : p.status;
                    return s === 'unavailable' || s === false || s === 'false';
                  }).sort(sortByPositionThenName);
                  
                  const unknown = nonInjuredPlayers.filter(p => {
                    const s = typeof p.status === 'string' ? p.status.toLowerCase() : p.status;
                    return s !== 'available' && s !== true && s !== 'true' && s !== 'unavailable' && s !== false && s !== 'false';
                  }).sort(sortByPositionThenName);
                  
                  // Sort by availability (available, unavailable, unknown), then injured at the bottom
                  const sortedPlayers = [
                    ...(showAvailable ? available : []),
                    ...(showUnavailable ? unavailable : []),
                    ...(showUnknown ? unknown : []),
                    ...injuredPlayers
                  ];
                  const gameDisplay = getGameDisplay(team.game);
                  return (
                    <Card key={`${team.teamId}-${team.gameIndex}`} data-testid={`team-availability-${team.teamId}-${team.gameIndex}`} sx={{
                      display: 'grid',
                      gridTemplateRows: { sm: 'subgrid' },
                      gridRow: { sm: 'span 2' }
                    }}>
                      <CardHeader
                        title={
                          <>
                            <Link 
                              to={`/teams/${team.teamSlug}`} 
                              style={{ textDecoration: 'none', color: 'inherit' }}
                            >
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                                  cursor: 'pointer'
                                }}
                              >
                                {team.teamName}
                              </Typography>
                            </Link>
                            {gameDisplay.details && (
                              <Typography variant="caption" color="text.secondary" component="div">
                                {gameDisplay.details}
                              </Typography>
                            )}
                          </>
                        }
                        subheader={
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CheckCircleIcon color="success" fontSize="small" />
                              <Typography variant="caption">{available.length}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CancelIcon color="error" fontSize="small" />
                              <Typography variant="caption">{unavailable.length}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <HelpIcon color="warning" fontSize="small" />
                              <Typography variant="caption">{unknown.length}</Typography>
                            </Box>
                            {injuredPlayers.length > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PersonalInjuryIcon color="disabled" fontSize="small" />
                                <Typography variant="caption">{injuredPlayers.length}</Typography>
                              </Box>
                            )}
                          </Box>
                        }
                        avatar={
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {team.teamName?.charAt(0) || 'T'}
                          </Avatar>
                        }
                      />
                      <CardContent sx={{ pt: 0 }}>
                        {sortedPlayers.length > 0 ? (
                          <List dense>
                            {sortedPlayers.map((player) => (
                              <ListItem key={player.playerId} sx={{ px: 0, opacity: player.isInjured ? 0.6 : 1 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  {player.isInjured ? <PersonalInjuryIcon color="disabled" /> : getAvailabilityIcon(player.status)}
                                </ListItemIcon>
                                <ListItemText 
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Chip 
                                        label={player.position} 
                                        size="small" 
                                        variant="outlined"
                                        color={player.isInjured ? 'default' : 'primary'}
                                        sx={{ fontSize: '0.65rem', height: 18, minWidth: 32 }}
                                        onClick={() => {/* do nothing */}}
                                      />
                                      <Link 
                                        to={`/player/${player.userTenantId}`}
                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                      >
                                        <Typography 
                                          variant="body2"
                                          sx={{ 
                                            '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                                            cursor: 'pointer',
                                            ...(player.isInjured && { textDecoration: 'line-through', color: 'text.disabled' })
                                          }}
                                        >
                                          {player.playerName}
                                        </Typography>
                                      </Link>
                                    </Box>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No player data available for this team.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Spare Availability Section */}
        {selectedDate && sparesEnabled && sparePool && !loading && (
          <Grid size={12}>
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Spare Availability
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Show:</Typography>
                <ToggleButtonGroup size="small">
                  <ToggleButton 
                    value="available" 
                    selected={showSpareAvailable}
                    onChange={() => setShowSpareAvailable(!showSpareAvailable)}
                    sx={{ gap: 0.5 }}
                  >
                    <CheckCircleIcon color={showSpareAvailable ? "success" : "disabled"} fontSize="small" />
                    <Typography variant="caption">Available</Typography>
                  </ToggleButton>
                  <ToggleButton 
                    value="unavailable" 
                    selected={showSpareUnavailable}
                    onChange={() => setShowSpareUnavailable(!showSpareUnavailable)}
                    sx={{ gap: 0.5 }}
                  >
                    <CancelIcon color={showSpareUnavailable ? "error" : "disabled"} fontSize="small" />
                    <Typography variant="caption">Not Available</Typography>
                  </ToggleButton>
                  <ToggleButton 
                    value="noResponse" 
                    selected={showSpareNoResponse}
                    onChange={() => setShowSpareNoResponse(!showSpareNoResponse)}
                    sx={{ gap: 0.5 }}
                  >
                    <HelpIcon color={showSpareNoResponse ? "warning" : "disabled"} fontSize="small" />
                    <Typography variant="caption">No Response</Typography>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <SpareAvailabilityTable
                leagueId={leagueId}
                canManage={canManageSparePool}
                canViewAll={canManageSparePool || isReporter}
                startDate={selectedDate.format('YYYY-MM-DD')}
                endDate={selectedDate.format('YYYY-MM-DD')}
                showAvailable={showSpareAvailable}
                showUnavailable={showSpareUnavailable}
                showNoResponse={showSpareNoResponse}
              />
            </Paper>
          </Grid>
        )}

        {selectedDate && sparesEnabled && sparePool && !loading && (
          <Grid size={12}>
            <SpareEmailComposer leagueId={leagueId} gameDate={selectedDate.format('YYYY-MM-DD')} />
          </Grid>
        )}

    </AdminLayout>
  );
}

export default PlayerAvailabilityPage;